import { useState } from 'react'
import type { ProjectImage } from '../api/types'
import { imageMeta } from '../lib/projectChat'
import { formatDateTime } from '../lib/time'

/** The Station Cameras tab of a project: the photos its stations' cameras
 *  have sent, newest first, with a viewer. They arrive by themselves: nobody
 *  uploads here, and a photo attached in Chat stays in Chat. */
export function ProjectImages({ stations, images, selected, onSelect }: {
  stations: { code: string; name: string }[]
  images: ProjectImage[] | undefined; selected: string | null
  onSelect: (id: string | null) => void
}) {
  const [station, setStation] = useState('')
  const shown = (images ?? []).filter((i) => !station || i.station.code === station)
  const current = shown.find((i) => i.id === selected) ?? shown[0] ?? null
  const at = current ? shown.indexOf(current) : -1

  return (
    <div role="tabpanel" aria-label="Station Cameras" className="pi-images">
      <div className="pi-tools">
        <label className="field inline">
          <span>Station</span>
          <select value={station} onChange={(e) => { setStation(e.target.value); onSelect(null) }}>
            <option value="">All stations</option>
            {stations.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
          </select>
        </label>
      </div>

      {images === undefined && <p className="muted">Loading…</p>}
      {current && (
        <figure className="pi-viewer">
          <a href={current.urls.preview} target="_blank" rel="noreferrer" className="pi-view">
            <img src={current.urls.preview} alt={current.caption} />
          </a>
          <figcaption>
            <span className="pi-caption">{current.caption}</span>
            <span>{current.station.name}</span>
            <span>Received {formatDateTime(current.created_at)}</span>
            <div className="pi-nav">
              <button type="button" className="button" disabled={at <= 0}
                      onClick={() => onSelect(shown[at - 1]!.id)}>Newer</button>
              <button type="button" className="button" disabled={at >= shown.length - 1}
                      onClick={() => onSelect(shown[at + 1]!.id)}>Older</button>
              <span className="muted">{at + 1} of {shown.length}</span>
            </div>
            <div className="pi-links">
              <a href={current.urls.original} download>Download</a>
            </div>
          </figcaption>
        </figure>
      )}

      {shown.length > 0 && (
        <div className="pi-grid">
          {shown.map((image) => (
            <button key={image.id} type="button" className="pi-thumb"
                    aria-pressed={image.id === current?.id} onClick={() => onSelect(image.id)}>
              <img src={image.urls.thumb} alt="" loading="lazy" />
              <span className="pi-thumb-caption">{image.caption}</span>
              <span className="pi-thumb-meta">{imageMeta(image)}</span>
            </button>
          ))}
        </div>
      )}
      {images !== undefined && shown.length === 0 && (
        <p className="pj-empty">
          No camera photos yet. A station with a camera adds its photos here as they arrive.
        </p>
      )}
    </div>
  )
}
